import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { Button, Modal, ModalBody, ModalHeader } from "@design-lab/shared/ui";

const MAX_INSTRUCTION_LENGTH = 1000;

type EditPinSummaryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (instruction: string) => void;
  pinIndex: number;
  initialInstruction: string;
  isLoading?: boolean;
};

export function EditPinSummaryModal({
  isOpen,
  onClose,
  onSave,
  pinIndex,
  initialInstruction,
  isLoading = false
}: EditPinSummaryModalProps) {
  const { isDesktop } = useResponsive();
  const [instruction, setInstruction] = useState(initialInstruction);

  useEffect(() => {
    if (isOpen) {
      setInstruction(initialInstruction);
    }
  }, [isOpen, initialInstruction]);

  const modalClassName = isDesktop
    ? "w-[420px] max-w-[90vw] h-auto"
    : "absolute bottom-0 left-0 right-0 flex h-auto max-h-[90vh] w-full flex-col rounded-b-none rounded-t-2xl";

  const handleSave = () => {
    onSave(instruction.trim());
  };

  const isSaveDisabled = isLoading || !instruction.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={modalClassName}
      backdropClassName="fixed z-50"
      closeOnBackdropClick={false}
      closeOnEsc={false}
    >
      <ModalHeader className="justify-start border-0 p-4 pb-0 text-base font-medium text-gray-900">
        Edit Pin {pinIndex} Summary
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 cursor-pointer"
          data-testid="edit-pin-summary-close-button"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>
      </ModalHeader>
      <ModalBody className="space-y-4">
        <div className="rounded-lg border border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
          <textarea
            value={instruction}
            onChange={e => setInstruction(e.target.value)}
            maxLength={MAX_INSTRUCTION_LENGTH}
            className="w-full resize-none border-none px-3 pt-3 text-base text-gray-700 focus:outline-none focus:ring-0"
            rows={5}
            placeholder="Enter pin instruction..."
            data-testid="edit-pin-summary-textarea"
            disabled={isLoading}
          />
          <div
            className="px-3 pb-2 text-right text-xs text-gray-500"
            data-testid="edit-pin-summary-char-count"
          >
            {instruction.length}/{MAX_INSTRUCTION_LENGTH}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onPress={onClose}
            className="w-full border-blue-600 px-4 py-2.5 text-blue-600 hover:border-blue-700 hover:bg-blue-50 hover:text-blue-700"
            isDisabled={isLoading}
            data-testid="edit-pin-summary-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onPress={handleSave}
            className="w-full bg-blue-600 px-4 py-2.5 text-white hover:bg-blue-700"
            isDisabled={isSaveDisabled}
            data-testid="edit-pin-summary-save-button"
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}
